import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { UserDetails } from './UserDetails';
import { SelectPlan } from './SelectPlan';
import { AddCreditCard } from './AddCreditCard';
import { FinishedScreen } from './FinishedScreen';
import { useDispatch, useSelector } from 'react-redux';
import { addCardClear, addSubscriptionClear, createUserClear, resumeSetup, startSetup } from '../../redux/actions/billingActions';
import { User } from '../../interface/User';
import { ApplicationState } from '../../interface/ApplicationState';
import { BillingApi } from '../../api/BillingApi';
import { Transition } from '../../animations/Transition';

interface SubscriptionModalProps
{
  open: boolean
  handleClose: () => void
  user: User
}

export const SubscriptionModal = ({ open, handleClose, user }: SubscriptionModalProps) =>
{
  const theme = useTheme()
  const dispatch = useDispatch()

  const [ email, setEmail ] = useState("")
  const [ name, setName ] = useState("")
  const [ selectedProductId, setSelectedProductId ] = useState("")
  const [ cvc, setCVC ] = useState("")
  const [ cardNumber, setCardNumber ] = useState("")
  const [ expiry, setExpiry ] = useState("")

  const addCardError = useSelector((state: ApplicationState) => state.billing.addCardError)
  const isAddingCreditCard = useSelector((state: ApplicationState) => state.billing.isAddingCreditCard)
  const addCardSuccess = useSelector((state: ApplicationState) => state.billing.addCardSuccess)
  const isCreatingUser = useSelector((state: ApplicationState) => state.billing.isCreatingUser)
  const createCustomerError = useSelector((state: ApplicationState) => state.billing.createCustomerError)
  const createCustomerSuccess = useSelector((state: ApplicationState) => state.billing.createCustomerSuccess)
  const isAddingSubscription = useSelector((state: ApplicationState) => state.billing.isAddingSubscription)
  const addSubscriptionSuccess = useSelector((state: ApplicationState) => state.billing.addSubscriptionSuccess)
  const stripeCustomer = useSelector((state: ApplicationState) => state.billing.stripeCustomer)
  const addSubscriptionError = useSelector((state: ApplicationState) => state.billing.addSubscriptionError)

  const setup = () =>
  {
    const expMonth = expiry.split("/")[0].trim()
    const expYear = expiry.split("/")[1].trim()

    if(!user.stripeCustomerId && !stripeCustomer) dispatch(startSetup(email, name, selectedProductId, cvc, expMonth, expYear, cardNumber))
    if(user.stripeCustomerId || stripeCustomer) dispatch(resumeSetup(selectedProductId, cvc, expMonth, expYear, cardNumber))
  }

  const addCardErrorResponse = () =>
  {
    try
    {
      // @ts-ignore
      return addCardError.message.response.data.error.message
    }
    catch(error)
    {
      return ""
    }
  }

  const createUserErrorResponse = () =>
  {
    try
    {
      // @ts-ignore
      return createCustomerError.message.response.data.error.message
    }
    catch(error)
    {
      return ""
    }
  }

  const addSubscriptionErrorResponse = () =>
  {
    try
    {
      // @ts-ignore
      return addSubscriptionError.message.response.data.error.message
    }
    catch(error)
    {
      return ""
    }
  }

  const areAllInputsValid = () =>
  {
    if(isCreatingUser || isAddingSubscription || isAddingCreditCard) return false
    if(email && name && selectedProductId && cardNumber.length === 19 && expiry.length === 7 && cvc.length === 3) return true
  }

  const getCustomer = async () =>
  {
    const ss = new BillingApi()
    const customerResponse = await ss.getCustomer()
    setEmail(customerResponse?.email || "")
    setName(customerResponse?.name || "")
  }

  const handleOnEnter = async () =>
  {
    dispatch(addCardClear())
    dispatch(createUserClear())
    dispatch(addSubscriptionClear())
    await getCustomer()
  }

  return(
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      TransitionProps={{ onEnter: handleOnEnter }}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      PaperProps={{ sx: styles.dialog(theme) }}
      aria-describedby="subscription-setup-dialog">
      <DialogTitle sx={styles.titleBar(theme)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box sx={styles.headerIcon(theme)}>
              <CreditCardRoundedIcon />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight={700}>Set Up Subscription</Typography>
            <Typography variant="body2" sx={styles.subtitle(theme)}>
              Choose a plan, confirm your billing identity, and add a payment method.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {!addSubscriptionSuccess && (
          <Stack spacing={2}>
            <Box sx={styles.hero(theme)}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} justifyContent="space-between">
                <Box>
                  <Typography variant="overline" sx={styles.eyebrow(theme)}>Checkout Flow</Typography>
                  <Typography variant="h6" fontWeight={700}>Complete your billing setup</Typography>
                  <Typography variant="body2" sx={styles.subtitle(theme)}>
                    Billing is created securely and attached to your AlgoTrader account.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip icon={<LocalOfferRoundedIcon />} label={selectedProductId ? "Plan selected" : "Choose a plan"} sx={styles.chip(theme)} />
                  <Chip icon={<PaymentsRoundedIcon />} label={cardNumber ? "Card details added" : "Add payment details"} sx={styles.chip(theme)} />
                </Stack>
              </Stack>
            </Box>

            <Box sx={styles.section(theme)}>
              <Typography variant="overline" sx={styles.sectionEyebrow(theme)}>Billing Identity</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Account Details</Typography>
              <UserDetails email={email} name={name} setEmail={setEmail} setName={setName} />
            </Box>

            <Box sx={styles.section(theme)}>
              <Typography variant="overline" sx={styles.sectionEyebrow(theme)}>Plan Selection</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Choose your subscription</Typography>
              <SelectPlan selectedProductId={selectedProductId} setSelectedProductId={setSelectedProductId} />
            </Box>

            <Box sx={styles.section(theme)}>
              <Typography variant="overline" sx={styles.sectionEyebrow(theme)}>Payment Method</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Add your card</Typography>
              <AddCreditCard setCardNumber={setCardNumber} cardNumber={cardNumber} cvc={cvc} setCVC={setCVC} expiry={expiry} setExpiry={setExpiry} />
            </Box>
          </Stack>
        )}

        { addSubscriptionSuccess && <FinishedScreen />}
      </DialogContent>

      <DialogActions sx={styles.actions(theme)}>
        <Button startIcon={<CloseIcon />} onClick={handleClose}>Close</Button>
        <Box flex={1} />
        { !addSubscriptionSuccess && <Button variant="contained" sx={styles.primaryButton} disabled={!areAllInputsValid()} startIcon={(isCreatingUser || isAddingSubscription || isAddingCreditCard) ? <CircularProgress size={16} /> : <AddIcon />} onClick={setup}>Add</Button> }
        { addSubscriptionSuccess && <Button variant="contained" sx={styles.primaryButton} startIcon={<CheckCircle />} onClick={handleClose}>Done</Button>}
      </DialogActions>

      <Snackbar open={!!addCardErrorResponse()}><Alert severity="error">{addCardErrorResponse()}</Alert></Snackbar>
      <Snackbar open={isAddingCreditCard}><Alert severity="info">Adding your card...</Alert></Snackbar>
      <Snackbar open={!!addCardSuccess}><Alert severity="success">Successfully Added Your Card!</Alert></Snackbar>
      <Snackbar open={!!createUserErrorResponse()}><Alert severity="error">{createUserErrorResponse()}</Alert></Snackbar>
      <Snackbar open={isCreatingUser}><Alert severity="info">Creating customer...</Alert></Snackbar>
      <Snackbar open={!!createCustomerSuccess}><Alert severity="success">Successfully Created Customer!</Alert></Snackbar>
      <Snackbar open={!!addSubscriptionErrorResponse()}><Alert severity="error">{addSubscriptionErrorResponse()}</Alert></Snackbar>
      <Snackbar open={isAddingSubscription}><Alert severity="info">Adding subscription...</Alert></Snackbar>
      <Snackbar open={!!addSubscriptionSuccess}><Alert severity="success">Successfully Assigned Subscription!</Alert></Snackbar>
    </Dialog>
  )
}

const styles = {
  dialog: (theme: any) => ({
    borderRadius: "18px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  titleBar: (theme: any) => ({
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  hero: (theme: any) => ({
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.68)} 0%, ${alpha("#0c1727", 0.82)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha("#e8f2ff", 0.9)} 100%)`,
  }),
  eyebrow: (theme: any) => ({
    display: "block",
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  sectionEyebrow: (theme: any) => ({
    display: "block",
    mb: 0.4,
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  subtitle: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
  }),
  chip: (theme: any) => ({
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  }),
  section: (theme: any) => ({
    p: 1.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
  actions: (theme: any) => ({
    px: 3,
    py: 2.25,
    borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02),
  }),
  primaryButton: {
    minWidth: 120,
    borderRadius: "12px",
    boxShadow: "none",
  },
}
